import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  LexicalLearningActivityProgress,
  LexicalLearningPracticeStage,
  LexicalLearningQuizStage,
  LexicalLearningReviewStage,
  LexicalLearningSession,
  LexicalLearningStage,
  LexicalLearningStageProgress,
} from '../lexical-learning/types.ts'
import { PRACTICE_STAGES, QUIZ_STAGES } from '../lexical-learning/types.ts'
import {
  buildQuizLexicalIds,
  emptyLexicalLearningProgress,
  emptyStageProgress,
  LEARN_PRONUNCIATION_PASS_SCORE,
  lexicalIdsForSelectedTokens,
  nextLexicalLearningStage,
  QUIZ_MASTERY_STREAK,
  representativeTokenKeys,
  uniqueIds,
  uniqueKnownIds,
} from '../lexical-learning/utils.ts'

interface LexicalLearningState {
  byPassageId: Record<string, LexicalLearningSession | undefined>
}

const initialState: LexicalLearningState = { byPassageId: {} }

function isPracticeStage(stage: LexicalLearningStage): stage is LexicalLearningPracticeStage {
  return PRACTICE_STAGES.includes(stage as LexicalLearningPracticeStage)
}

function parseStageProgress(value: unknown, targetIds: string[]): LexicalLearningStageProgress {
  const fallback = emptyStageProgress(targetIds)
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return fallback
  const raw = value as Record<string, unknown>
  const status = ['not_started', 'in_progress', 'completed', 'skipped'].includes(String(raw.status))
    ? raw.status as LexicalLearningStageProgress['status']
    : fallback.status
  const queue = Array.isArray(raw.queue)
    ? uniqueKnownIds(raw.queue.filter((id): id is string => typeof id === 'string'), targetIds)
    : fallback.queue
  const completedIds = Array.isArray(raw.completed_ids)
    ? uniqueKnownIds(raw.completed_ids.filter((id): id is string => typeof id === 'string'), targetIds)
    : []
  const bestScores = typeof raw.best_scores === 'object' && raw.best_scores !== null && !Array.isArray(raw.best_scores)
    ? Object.fromEntries(Object.entries(raw.best_scores).flatMap(([id, score]) => (
      targetIds.includes(id) && Number.isFinite(score) && Number(score) >= 0
        ? [[id, Math.min(100, Number(score))]]
        : []
    )))
    : {}
  const streaks = typeof raw.streaks === 'object' && raw.streaks !== null && !Array.isArray(raw.streaks)
    ? Object.fromEntries(Object.entries(raw.streaks).flatMap(([id, streak]) => (
      targetIds.includes(id) && Number.isSafeInteger(streak) && Number(streak) >= 0
        ? [[id, Math.min(QUIZ_MASTERY_STREAK, Number(streak))]]
        : []
    )))
    : {}
  const revealCounts = typeof raw.reveal_counts === 'object' && raw.reveal_counts !== null && !Array.isArray(raw.reveal_counts)
    ? Object.fromEntries(Object.entries(raw.reveal_counts).flatMap(([id, count]) => (
      targetIds.includes(id) && Number.isSafeInteger(count) && Number(count) >= 0
        ? [[id, Number(count)]]
        : []
    )))
    : {}
  return {
    status,
    queue,
    current_index: Number.isSafeInteger(raw.current_index)
      ? Math.max(0, Math.min(targetIds.length - 1, Number(raw.current_index)))
      : 0,
    completed_ids: completedIds,
    best_scores: bestScores,
    streaks,
    reveal_counts: revealCounts,
    review_rounds: Number.isSafeInteger(raw.review_rounds) && Number(raw.review_rounds) >= 0 ? Number(raw.review_rounds) : 0,
  }
}

function hydrateSession(
  passageId: string,
  value: Record<string, unknown>,
  passageLexicalIds: string[],
  tokenLexicalIdsByKey: Record<string, string[]>,
): LexicalLearningSession {
  const empty = emptyLexicalLearningProgress()
  const selectedIds = Array.isArray(value.selected_lexical_ids)
    ? uniqueKnownIds(value.selected_lexical_ids.filter((id): id is string => typeof id === 'string'), passageLexicalIds)
    : []
  const savedTokenKeys = Array.isArray(value.selected_token_keys)
    ? uniqueIds(value.selected_token_keys.filter((key): key is string => typeof key === 'string'))
      .filter((key) => key in tokenLexicalIdsByKey)
    : []
  const selectedTokenKeys = savedTokenKeys.length > 0
    ? savedTokenKeys
    : representativeTokenKeys(selectedIds, tokenLexicalIdsByKey)
  const quizIds = Array.isArray(value.quiz_lexical_ids)
    ? uniqueKnownIds(value.quiz_lexical_ids.filter((id): id is string => typeof id === 'string'), passageLexicalIds)
    : buildQuizLexicalIds(selectedIds, passageLexicalIds)
  const quizAvailable = selectedIds.length > 0 && quizIds.length >= 4
  const stagesValue = typeof value.stages === 'object' && value.stages !== null && !Array.isArray(value.stages)
    ? value.stages as Record<string, unknown>
    : {}
  const stages = Object.fromEntries(PRACTICE_STAGES.map((stage) => {
    const targets = QUIZ_STAGES.includes(stage as LexicalLearningQuizStage) ? quizIds : selectedIds
    const progress = parseStageProgress(stagesValue[stage], targets)
    if (!quizAvailable && QUIZ_STAGES.includes(stage as LexicalLearningQuizStage)) progress.status = 'skipped'
    return [stage, progress]
  })) as LexicalLearningActivityProgress['stages']
  const requestedStage = typeof value.current_stage === 'string' ? value.current_stage as LexicalLearningStage : empty.current_stage
  let currentStage = requestedStage === 'complete' || requestedStage === 'skim' || isPracticeStage(requestedStage)
    ? requestedStage
    : 'skim'
  if (selectedIds.length === 0 && value.completed !== true) currentStage = 'skim'
  if (!quizAvailable && QUIZ_STAGES.includes(currentStage as LexicalLearningQuizStage)) currentStage = 'review_speaking'
  return {
    version: 1,
    passage_id: passageId,
    current_stage: selectedIds.length === 0 && value.completed === true ? 'complete' : currentStage,
    selected_token_keys: selectedTokenKeys,
    selected_lexical_ids: selectedIds,
    skim_draft_token_keys: selectedTokenKeys,
    skim_draft_lexical_ids: selectedIds,
    quiz_lexical_ids: quizIds,
    quiz_available: quizAvailable,
    stages,
    completed: value.completed === true,
    hydrated: true,
    revision: 0,
  }
}

function targetsFor(session: LexicalLearningSession, stage: LexicalLearningPracticeStage): string[] {
  return QUIZ_STAGES.includes(stage as LexicalLearningQuizStage)
    ? session.quiz_lexical_ids
    : session.selected_lexical_ids
}

function beginStage(session: LexicalLearningSession, stage: LexicalLearningPracticeStage) {
  const progress = session.stages[stage]
  if (progress.status === 'not_started') progress.status = 'in_progress'
  if (progress.queue.length === 0 && progress.status === 'in_progress' && progress.completed_ids.length === 0) {
    progress.queue = [...targetsFor(session, stage)]
  }
}

function advance(session: LexicalLearningSession) {
  const next = nextLexicalLearningStage(session.current_stage, session.quiz_available)
  session.current_stage = next
  if (next === 'complete') {
    session.completed = true
  } else if (isPracticeStage(next)) {
    beginStage(session, next)
  }
}

function resetStage(session: LexicalLearningSession, stage: LexicalLearningPracticeStage) {
  const reviewRounds = session.stages[stage].review_rounds
  session.stages[stage] = {
    ...emptyStageProgress(targetsFor(session, stage)),
    status: 'in_progress',
    review_rounds: stage.startsWith('review_') ? reviewRounds + 1 : 0,
  }
}

function rotateQueue(progress: LexicalLearningStageProgress, lexicalId: string, remove: boolean) {
  progress.queue = progress.queue.filter((id) => id !== lexicalId)
  if (!remove) progress.queue.push(lexicalId)
}

const lexicalLearningSlice = createSlice({
  name: 'lexicalLearning',
  initialState,
  reducers: {
    clearLexicalLearningSession: (state, action: PayloadAction<{ passageId: string }>) => {
      delete state.byPassageId[action.payload.passageId]
    },
    hydrateLexicalLearning: (state, action: PayloadAction<{
      passageId: string
      value: Record<string, unknown>
      passageLexicalIds: string[]
      tokenLexicalIdsByKey: Record<string, string[]>
    }>) => {
      state.byPassageId[action.payload.passageId] = hydrateSession(
        action.payload.passageId,
        action.payload.value,
        action.payload.passageLexicalIds,
        action.payload.tokenLexicalIdsByKey,
      )
    },
    openLexicalLearningSkim: (state, action: PayloadAction<{ passageId: string }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      session.skim_draft_token_keys = [...session.selected_token_keys]
      session.skim_draft_lexical_ids = [...session.selected_lexical_ids]
      session.current_stage = 'skim'
      session.revision += 1
    },
    toggleLexicalLearningSkimToken: (state, action: PayloadAction<{
      passageId: string
      tokenKey: string
      tokenLexicalIdsByKey: Record<string, string[]>
    }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      if (!(action.payload.tokenKey in action.payload.tokenLexicalIdsByKey)) return
      const selected = new Set(session.skim_draft_token_keys)
      if (selected.has(action.payload.tokenKey)) selected.delete(action.payload.tokenKey)
      else selected.add(action.payload.tokenKey)
      session.skim_draft_token_keys = [...selected]
      session.skim_draft_lexical_ids = lexicalIdsForSelectedTokens(
        session.skim_draft_token_keys,
        action.payload.tokenLexicalIdsByKey,
      )
      session.revision += 1
    },
    confirmLexicalLearningSkim: (state, action: PayloadAction<{
      passageId: string
      passageLexicalIds: string[]
      tokenLexicalIdsByKey: Record<string, string[]>
    }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      const selectedTokenKeys = session.skim_draft_token_keys.filter((key) => key in action.payload.tokenLexicalIdsByKey)
      const selectedIds = uniqueKnownIds(
        lexicalIdsForSelectedTokens(selectedTokenKeys, action.payload.tokenLexicalIdsByKey),
        action.payload.passageLexicalIds,
      )
      const quizIds = buildQuizLexicalIds(selectedIds, action.payload.passageLexicalIds)
      session.selected_token_keys = selectedTokenKeys
      session.selected_lexical_ids = selectedIds
      session.skim_draft_token_keys = [...selectedTokenKeys]
      session.skim_draft_lexical_ids = [...selectedIds]
      session.quiz_lexical_ids = quizIds
      session.quiz_available = quizIds.length >= 4
      session.completed = selectedIds.length === 0

      for (const stage of PRACTICE_STAGES) {
        const quizStage = QUIZ_STAGES.includes(stage as LexicalLearningQuizStage)
        session.stages[stage] = quizStage && !session.quiz_available
          ? { ...emptyStageProgress(), status: 'skipped' }
          : emptyStageProgress(quizStage ? quizIds : selectedIds)
      }

      if (selectedIds.length === 0) session.current_stage = 'complete'
      else {
        session.current_stage = 'learn_pronunciation'
        beginStage(session, 'learn_pronunciation')
      }
      session.revision += 1
    },
    goToLexicalLearningStage: (state, action: PayloadAction<{ passageId: string; stage: LexicalLearningStage }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session || (action.payload.stage !== 'skim' && session.selected_lexical_ids.length === 0)) return
      if (QUIZ_STAGES.includes(action.payload.stage as LexicalLearningQuizStage) && !session.quiz_available) return
      session.current_stage = action.payload.stage
      session.completed = false
      if (isPracticeStage(action.payload.stage)) {
        if (session.stages[action.payload.stage].status === 'completed' || session.stages[action.payload.stage].status === 'skipped') {
          resetStage(session, action.payload.stage)
        } else beginStage(session, action.payload.stage)
      }
      session.revision += 1
    },
    advanceLexicalLearningStage: (state, action: PayloadAction<{ passageId: string }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      advance(session)
      session.revision += 1
    },
    skipLexicalLearningStage: (state, action: PayloadAction<{ passageId: string; stage: LexicalLearningPracticeStage }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      session.stages[action.payload.stage].status = 'skipped'
      session.stages[action.payload.stage].queue = []
      if (session.current_stage === action.payload.stage) advance(session)
      session.revision += 1
    },
    setCurrentPronunciationLexical: (state, action: PayloadAction<{ passageId: string; index: number }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      const progress = session.stages.learn_pronunciation
      progress.current_index = Math.max(0, Math.min(session.selected_lexical_ids.length - 1, action.payload.index))
      session.revision += 1
    },
    recordLexicalPronunciationScore: (state, action: PayloadAction<{ passageId: string; lexicalId: string; score: number }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session || !session.selected_lexical_ids.includes(action.payload.lexicalId)) return
      const progress = session.stages.learn_pronunciation
      const score = Math.max(0, Math.min(100, action.payload.score))
      progress.best_scores[action.payload.lexicalId] = Math.max(progress.best_scores[action.payload.lexicalId] ?? 0, score)
      if (score >= LEARN_PRONUNCIATION_PASS_SCORE) {
        progress.queue = progress.queue.filter((id) => id !== action.payload.lexicalId)
        if (!progress.completed_ids.includes(action.payload.lexicalId)) progress.completed_ids.push(action.payload.lexicalId)
        if (progress.queue.length === 0) progress.status = 'completed'
      }
      session.revision += 1
    },
    finishPronunciationLearningStage: (state, action: PayloadAction<{ passageId: string }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      const progress = session.stages.learn_pronunciation
      if (progress.queue.length > 0) return
      progress.status = 'completed'
      if (session.current_stage === 'learn_pronunciation') advance(session)
      session.revision += 1
    },
    answerLexicalLearningQuiz: (state, action: PayloadAction<{
      passageId: string
      stage: LexicalLearningQuizStage
      lexicalId: string
      correct: boolean
    }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      const progress = session.stages[action.payload.stage]
      if (!progress.queue.includes(action.payload.lexicalId)) return
      const score = action.payload.correct
        ? Math.min(QUIZ_MASTERY_STREAK, (progress.streaks[action.payload.lexicalId] ?? 0) + 1)
        : Math.max(0, (progress.streaks[action.payload.lexicalId] ?? 0) - 1)
      progress.streaks[action.payload.lexicalId] = score
      rotateQueue(progress, action.payload.lexicalId, score >= QUIZ_MASTERY_STREAK)
      if (progress.queue.length === 0) {
        progress.status = 'completed'
        if (session.current_stage === action.payload.stage) advance(session)
      }
      session.revision += 1
    },
    revealLexicalReviewHint: (state, action: PayloadAction<{
      passageId: string
      stage: LexicalLearningReviewStage
      lexicalId: string
    }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      const progress = session.stages[action.payload.stage]
      progress.reveal_counts[action.payload.lexicalId] = (progress.reveal_counts[action.payload.lexicalId] ?? 0) + 1
      session.revision += 1
    },
    answerLexicalLearningReview: (state, action: PayloadAction<{
      passageId: string
      stage: LexicalLearningReviewStage
      lexicalId: string
      correct: boolean
    }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      const progress = session.stages[action.payload.stage]
      if (!progress.queue.includes(action.payload.lexicalId)) return
      const assisted = (progress.reveal_counts[action.payload.lexicalId] ?? 0) > 0
      delete progress.reveal_counts[action.payload.lexicalId]
      rotateQueue(progress, action.payload.lexicalId, action.payload.correct && !assisted)
      if (progress.queue.length === 0) {
        progress.status = 'completed'
        progress.review_rounds += 1
      }
      session.revision += 1
    },
    restartLexicalLearningStage: (state, action: PayloadAction<{ passageId: string; stage: LexicalLearningPracticeStage }>) => {
      const session = state.byPassageId[action.payload.passageId]
      if (!session) return
      resetStage(session, action.payload.stage)
      session.current_stage = action.payload.stage
      session.completed = false
      session.revision += 1
    },
  },
})

export function serializeLexicalLearningSession(session: LexicalLearningSession): LexicalLearningActivityProgress {
  return {
    version: 1,
    current_stage: session.current_stage,
    selected_token_keys: session.selected_token_keys,
    selected_lexical_ids: session.selected_lexical_ids,
    quiz_lexical_ids: session.quiz_lexical_ids,
    quiz_available: session.quiz_available,
    stages: session.stages,
    completed: session.completed,
  }
}

export const {
  clearLexicalLearningSession,
  hydrateLexicalLearning,
  openLexicalLearningSkim,
  toggleLexicalLearningSkimToken,
  confirmLexicalLearningSkim,
  goToLexicalLearningStage,
  advanceLexicalLearningStage,
  skipLexicalLearningStage,
  setCurrentPronunciationLexical,
  recordLexicalPronunciationScore,
  finishPronunciationLearningStage,
  answerLexicalLearningQuiz,
  revealLexicalReviewHint,
  answerLexicalLearningReview,
  restartLexicalLearningStage,
} = lexicalLearningSlice.actions

export default lexicalLearningSlice.reducer
